import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Globe, Server, Network, Truck, Layers, ArrowRight, Check } from 'lucide-react';
import { SOLUTIONS, EXTENDED_SOLUTIONS } from '../config/siteContent';

const ICONS = { Globe, Server, Network, Truck, Layers };
const byId = Object.fromEntries(SOLUTIONS.map((s) => [s.id, s]));
const HYBRID = EXTENDED_SOLUTIONS['hybrid-van'];

/**
 * SolutionsGrid - homepage solutions section.
 *
 * Layout (2026-08): Hybrid VAN is the flagship, so it gets a full-width
 * gradient-bordered spotlight card at the top (its story + every feature + a
 * strong CTA). Below, a 3-card row: "Managed & Web EDI" (Managed EDI Services +
 * iExchangeWeb merged into one card, each keeping its own detail link), plus
 * VAN Services and Supplier EDI. OFTP2 lives on the /solutions page only.
 */

/** Cursor-follow spotlight wrapper (CSS in index.css → .spotlight-card). */
const SpotlightCard = ({ children, className = '', ...rest }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const node = ref.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    node.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
    node.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
  };
  return (
    <div ref={ref} onMouseMove={onMove} className={`spotlight-card card-elevated ${className}`} {...rest}>
      {children}
    </div>
  );
};

const FeatureItem = ({ children }) => (
  <li className="flex items-start gap-2 text-sm text-text-secondary">
    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-accent-soft flex items-center justify-center mt-0.5">
      <Check size={10} className="text-accent-primary" strokeWidth={3} />
    </span>
    <span>{children}</span>
  </li>
);

/** Compact card for the lower row (VAN, Supplier EDI). */
const SolutionCard = ({ solution }) => {
  const Icon = ICONS[solution.icon] || Globe;
  return (
    <SpotlightCard className="h-full p-8 flex flex-col">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-gradient-sky shadow-glow-sky">
        <Icon size={22} className="text-white" strokeWidth={2.2} />
      </div>
      <h3 className="font-display font-bold text-xl text-text-primary mb-1 tracking-tight">
        {solution.title}
      </h3>
      <p className="text-xs font-mono uppercase tracking-wider text-accent-deep mb-3">
        {solution.subtitle}
      </p>
      <p className="text-sm text-text-secondary leading-relaxed mb-5">
        {solution.description}
      </p>
      <ul className="space-y-2 mb-6">
        {solution.features.slice(0, 4).map((f, i) => (
          <FeatureItem key={i}>{f}</FeatureItem>
        ))}
      </ul>
      <Link
        to={`/solutions/${solution.id}`}
        className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-accent-primary hover:text-accent-deep transition-colors group/link"
      >
        Learn more
        <ArrowRight size={14} className="transition-transform group-hover/link:translate-x-1" />
      </Link>
    </SpotlightCard>
  );
};

const SolutionsGrid = () => {
  const HybridIcon = ICONS[HYBRID.icon] || Layers;

  return (
    <section id="solutions" className="relative py-24 lg:py-32 bg-bg-canvas">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14 max-w-3xl xl:max-w-none mx-auto"
        >
          <p className="eyebrow">Solutions</p>
          {/* xl:whitespace-nowrap keeps this on one line at full size on
              laptops/desktops; wraps normally below xl so phones never overflow. */}
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-text-primary mb-5 tracking-[-0.02em] text-balance xl:whitespace-nowrap">
            <span className="text-gradient-sky">Built for</span>{' '}
            the way you do business
          </h2>
          <p className="text-lg text-text-secondary text-pretty">
            From web-based EDI to fully managed integration - pick the path that
            fits your team, your scale, and your roadmap.
          </p>
        </motion.div>

        {/* FLAGSHIP: Hybrid VAN — full-width gradient-bordered spotlight */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8 lg:mb-10"
        >
          <div className="rounded-3xl p-[1.5px] bg-gradient-to-br from-accent-primary via-accent-bright to-accent-deep shadow-lifted">
            <div className="relative rounded-3xl bg-white overflow-hidden p-8 lg:p-12">
              <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-accent-soft/50 blur-3xl" />
              <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                {/* Left: story + CTAs */}
                <div>
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-gradient-sky shadow-glow-sky flex-shrink-0">
                      <HybridIcon size={26} className="text-white" strokeWidth={2.2} />
                    </div>
                    <div>
                      <h3 className="font-display font-extrabold text-3xl text-text-primary tracking-tight leading-none">
                        {HYBRID.title}
                      </h3>
                      <p className="text-xs font-mono uppercase tracking-wider text-accent-deep mt-1.5">
                        {HYBRID.subtitle}
                      </p>
                    </div>
                  </div>
                  <p className="text-text-secondary leading-relaxed mb-7">
                    {HYBRID.description}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link to="/solutions/hybrid-van" className="btn-primary group">
                      Explore Hybrid VAN
                      <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                    </Link>
                    <Link to="/contact" className="btn-secondary">
                      Schedule a demo
                    </Link>
                  </div>
                </div>

                {/* Right: full feature list in two columns */}
                <div className="lg:pl-10 lg:border-l lg:border-border-subtle">
                  <p className="text-xs font-mono uppercase tracking-wider text-text-muted mb-4">
                    What you get
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    {HYBRID.features.map((f, i) => (
                      <FeatureItem key={i}>{f}</FeatureItem>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Lower row: merged Managed & Web EDI + VAN + Supplier EDI */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {/* Merged: Managed EDI Services + iExchangeWeb */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <SpotlightCard className="h-full p-8 flex flex-col">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-gradient-sky shadow-glow-sky">
                <Server size={22} className="text-white" strokeWidth={2.2} />
              </div>
              <h3 className="font-display font-bold text-xl text-text-primary mb-1 tracking-tight">
                Managed & Web EDI
              </h3>
              <p className="text-xs font-mono uppercase tracking-wider text-accent-deep mb-3">
                Hosted or self-serve
              </p>
              <p className="text-sm text-text-secondary leading-relaxed mb-5">
                Two ways to run everyday EDI: a fully hosted service integrated
                with your ERP, or a simple browser-based portal you can go live
                on in days.
              </p>
              <ul className="space-y-2 mb-6">
                <FeatureItem>Fully hosted or web-based</FeatureItem>
                <FeatureItem>Integrated with your ERP</FeatureItem>
                <FeatureItem>Go live in days</FeatureItem>
                <FeatureItem>Monthly subscription, no long-term contract</FeatureItem>
              </ul>
              <div className="mt-auto flex flex-col gap-2.5">
                <Link
                  to="/solutions/managed-edi"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-primary hover:text-accent-deep transition-colors group/link"
                >
                  Managed EDI Services
                  <ArrowRight size={14} className="transition-transform group-hover/link:translate-x-1" />
                </Link>
                <Link
                  to="/solutions/iexchangeweb"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-primary hover:text-accent-deep transition-colors group/link"
                >
                  iExchangeWeb
                  <ArrowRight size={14} className="transition-transform group-hover/link:translate-x-1" />
                </Link>
              </div>
            </SpotlightCard>
          </motion.div>

          {['van', 'supplier-edi'].map((id, idx) => (
            <motion.div
              key={id}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, delay: (idx + 1) * 0.08, ease: [0.16, 1, 0.3, 1] }}
            >
              <SolutionCard solution={byId[id]} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionsGrid;
